import CloseIcon from '@mui/icons-material/Close'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import Box from '@mui/material/Box'
import Modal from '@mui/material/Modal'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import { MouseEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Document, Page } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import { pdfjs } from 'react-pdf'

import { Button } from '~/components/Atoms/Button'
import { DownloadContext } from '~/components/Molecules/DownloadContext'

import ReactGA from '~/lib/analytics'
import { useDownloadStat } from '~/lib/stats'

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString()

export function PDFViewer({
  id,
  name,
  open,
  setOpen,
}: {
  id: string
  name: string
  open: boolean
  setOpen: (open: boolean) => void
}) {
  const { mutateAsync, data: downloadData } = useDownloadStat()
  const [numPages, setNumPages] = useState<number>()
  const [url, setUrl] = useState<string>()
  const [openContext, setOpenContext] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const { t } = useTranslation()
  const theme = useTheme()

  function onDocumentLoadSuccess({ numPages }: { numPages: number }): void {
    ReactGA.event({ action: 'pdf_view', category: 'engagement', label: 'pdf_view' })
    setNumPages(numPages)
  }

  useEffect(() => {
    if (id) {
      mutateAsync(id)
    }
  }, [id, mutateAsync])

  useEffect(() => {
    if (downloadData && downloadData.url_pdf) {
      setUrl(downloadData.url_pdf)
    }
  }, [downloadData])

  return (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      sx={{ outline: 0, '& .MuiBackdrop-root': { bgcolor: 'rgba(0, 0, 0, 0.7)' } }}
    >
      <Box display="flex" flexDirection="column" gap={theme.spacing['6XL']} sx={{ pointerEvents: 'none' }}>
        <Box
          display="flex"
          width="100%"
          justifyContent="space-between"
          padding={theme.spacing.ML}
          bgcolor={theme.palette.common.white}
          alignItems={'center'}
        >
          <Box display="flex" flex={1} />
          <Typography textAlign="center" flex={2} variant="h3">
            {name}
          </Typography>
          <Box display="flex" justifyContent="flex-end" flex={1} gap={theme.spacing.L}>
            <Button
              onClick={(event: MouseEvent<HTMLButtonElement>) => {
                setOpenContext(true)
                setAnchorEl(event.currentTarget)
              }}
              variant="contained"
              endIcon={<DownloadOutlinedIcon />}
              label={t('common:download')}
            />
            <Button
              onClick={() => setOpen(false)}
              variant="outlined"
              endIcon={<CloseIcon />}
              label={t('common:close')}
            />
          </Box>
        </Box>
        <Box
          padding={'0 32px'}
          bgcolor={'transparent'}
          justifyContent="center"
          flexDirection="column"
          overflow={'scroll'}
          maxHeight={'100vh'}
          margin={'auto'}
          width={'75%'}
          sx={{
            pointerEvents: 'auto',
            display: 'block',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            '& .react-pdf__Document': {
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: theme.spacing['6XL'],
              '.react-pdf__Page': {
                borderRadius: theme.radius.L,
                overflow: 'hidden',
              },
            },
          }}
        >
          <Document key={url} file={url} onLoadSuccess={onDocumentLoadSuccess}>
            {Array.from(new Array(numPages), (el, index) => (
              <Page key={`page_${index + 1}`} pageNumber={index + 1} />
            ))}
          </Document>
        </Box>
        <DownloadContext anchorEl={anchorEl} id={id} open={openContext} setOpen={setOpenContext} />
      </Box>
    </Modal>
  )
}
